import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import ConfirmModal from "./components/ConfirmModal";
import ShareUploadLinkModal from "./components/ShareUploadLinkModal";
import AssignStaffModal from "./components/AssignStaffModal";
import SubscriptionPausedModal from "./components/SubscriptionPausedModal";
import { useUploadManager } from "./components/UploadManager";
import API_BASE_URL from "./config";
import "./Events.css";
import { clearAuthStorage } from "./auth/logout";

function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    eventName: "",
    groomName: "",
    bridalName: "",
    eventDate: "",
    eventTypeId: "",
  });
  const [wantUncompressed, setWantUncompressed] = useState(false);
  const [uncompressedQuota, setUncompressedQuota] = useState(null); // { allowed, max, used, remaining }
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  // Event types
  const [eventTypes, setEventTypes] = useState([]);
  const [typeSearch, setTypeSearch] = useState("");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef(null);

  // Inline editing
  const [inlineEdit, setInlineEdit] = useState(null); // event id being edited inline

  // Delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState(null); // event pending deletion
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // "Share with co-worker" modal (link + PIN upload access for one event)
  const [shareTarget, setShareTarget] = useState(null);

  // "Assign staff" modal (owner maps an event to team members). canAssign is
  // true only for the studio owner — detected by the owner-only team endpoint
  // returning 200; a team member gets 403 and never sees the assign control.
  const [assignTarget, setAssignTarget] = useState(null);
  const [canAssign, setCanAssign] = useState(false);

  // Subscription-expired popup (create-event 402) — routes to wallet/renew.
  const [pausedInfo, setPausedInfo] = useState(null); // { message, locked }

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");
  const uploadManager = useUploadManager();
  const wasUploadingRef = useRef({});

  const fetchEventTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/event-types`);
      const data = await res.json();
      setEventTypes(data.eventTypes || []);
    } catch { /* ignore */ }
  }, []);

  // Only Pro Max shows a non-zero max — this is what hides the checkbox entirely
  // for plans that don't include it, rather than showing it and always rejecting.
  const fetchUncompressedQuota = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${userId}/uncompressed-quota`);
      const data = await res.json();
      setUncompressedQuota(data);
    } catch { /* ignore — checkbox just stays hidden */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
    fetchUncompressedQuota();
    // Owner-only: the team endpoint returns 200 for the studio owner and 403 for
    // a team member. Use that to decide whether to show the "Assign staff" control.
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/team/members`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCanAssign(res.ok);
      } catch {
        setCanAssign(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh photo counts/thumbnails when a background upload (started from this
  // page or another event's page) finishes, so cards don't go stale silently.
  useEffect(() => {
    const uploads = uploadManager.uploads;
    let finished = false;
    Object.keys(uploads).forEach((id) => {
      const wasUploading = wasUploadingRef.current[id];
      const isUploading = !!uploads[id].isUploading;
      if (wasUploading && !isUploading) finished = true;
      wasUploadingRef.current[id] = isUploading;
    });
    if (finished) fetchEvents();
  }, [uploadManager.uploads]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clear messages
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(""), 4000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); }
  }, [error]);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setEvents(data.events || []);
    } catch (err) {
      setError("Failed to fetch events");
    } finally {
      setFetchLoading(false);
    }
  };

  // Event type dropdown helpers
  const filteredTypes = eventTypes.filter((et) =>
    et.name.toLowerCase().includes(typeSearch.toLowerCase())
  );
  const exactTypeMatch = eventTypes.some((et) => et.name.toLowerCase() === typeSearch.trim().toLowerCase());

  const selectEventType = (et) => {
    setFormData({ ...formData, eventTypeId: et.id });
    setTypeSearch(et.name);
    setTypeDropdownOpen(false);
  };

  const createAndSelectType = async () => {
    if (!typeSearch.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/event-types`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: typeSearch.trim(), userId }),
      });
      const data = await res.json();
      fetchEventTypes();
      setFormData({ ...formData, eventTypeId: data.eventType.id });
      setTypeSearch(data.eventType.name);
      setTypeDropdownOpen(false);
    } catch {
      setError("Failed to create event type");
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (!formData.eventName || !formData.groomName || !formData.bridalName || !formData.eventDate) {
      setError("All fields are required");
      setLoading(false);
      return;
    }

    try {
      if (editingId) {
        const response = await fetch(`${API_BASE_URL}/api/events/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            eventName: formData.eventName,
            groomName: formData.groomName,
            bridalName: formData.bridalName,
            eventDate: formData.eventDate,
            eventTypeId: formData.eventTypeId || null,
          }),
        });
        if (!response.ok) throw new Error("Failed to update event");
        setSuccess("Event updated successfully!");
        setEditingId(null);
      } else {
        const response = await fetch(`${API_BASE_URL}/api/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            eventName: formData.eventName,
            groomName: formData.groomName,
            bridalName: formData.bridalName,
            eventDate: formData.eventDate,
            eventTypeId: formData.eventTypeId || null,
            uncompressed: wantUncompressed,
          }),
        });
        if (response.status === 402) {
          const data = await response.json().catch(() => ({}));
          setPausedInfo({ message: data.message, locked: !!data.locked });
          setLoading(false);
          return;
        }
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || "Failed to create event");
        }
        setSuccess("Event created successfully! Click on the event to add photos.");
        if (wantUncompressed) fetchUncompressedQuota();
      }

      setFormData({ eventName: "", groomName: "", bridalName: "", eventDate: "", eventTypeId: "" });
      setTypeSearch("");
      setWantUncompressed(false);
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setFormData({
      eventName: event.event_name,
      groomName: event.groom_name,
      bridalName: event.bridal_name,
      eventDate: event.event_date ? event.event_date.split("T")[0] : "",
      eventTypeId: event.event_type_id || "",
    });
    setTypeSearch(event.event_type_name || "");
    setEditingId(event.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Inline editing
  const startInlineEdit = (event, e) => {
    e.stopPropagation();
    setInlineEdit({
      id: event.id,
      eventName: event.event_name,
      groomName: event.groom_name,
      bridalName: event.bridal_name,
      eventDate: event.event_date ? event.event_date.split("T")[0] : "",
      eventTypeId: event.event_type_id || "",
      eventTypeName: event.event_type_name || "",
    });
  };

  const handleInlineChange = (field, value) => {
    setInlineEdit({ ...inlineEdit, [field]: value });
  };

  const saveInlineEdit = async (e) => {
    e.stopPropagation();
    if (!inlineEdit) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${inlineEdit.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventName: inlineEdit.eventName,
          groomName: inlineEdit.groomName,
          bridalName: inlineEdit.bridalName,
          eventDate: inlineEdit.eventDate,
          eventTypeId: inlineEdit.eventTypeId || null,
        }),
      });
      if (!response.ok) throw new Error("Failed to update");
      setSuccess("Event updated!");
      setInlineEdit(null);
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const cancelInlineEdit = (e) => {
    e.stopPropagation();
    setInlineEdit(null);
  };

  const handleOpenEventDetails = (event) => {
    if (inlineEdit && inlineEdit.id === event.id) return;
    navigate(`/events/${event.id}`);
  };

  const handleDelete = (event, e) => {
    e.stopPropagation();
    setDeleteError("");
    setDeleteTarget(event);
  };

  const handleShare = (event, e) => {
    e.stopPropagation();
    setShareTarget(event);
  };

  const handleAssign = (event, e) => {
    e.stopPropagation();
    setAssignTarget(event);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    const eventId = deleteTarget.id;
    setDeleting(true);
    setDeleteError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to delete event");
      }
      if (inlineEdit?.id === eventId) setInlineEdit(null);
      // Refresh the list before closing the modal so the card is already gone
      // when the overlay lifts — no stale event left in the DOM.
      await fetchEvents();
      setDeleteTarget(null);
      setSuccess("Event deleted successfully!");
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ eventName: "", groomName: "", bridalName: "", eventDate: "", eventTypeId: "" });
    setTypeSearch("");
    setWantUncompressed(false);
    setError("");
  };

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  const getEventTypeName = (id) => {
    const et = eventTypes.find((t) => t.id === id);
    return et ? et.name : "";
  };

  return (
    <div className="events-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="events-main-content">
        <header className="events-header">
          <div className="events-header-left">
            <button
              className="events-toggle-sidebar"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              ☰
            </button>
            <h1 className="events-page-title">Events Management</h1>
          </div>
          <div className="events-header-right">
            <button className="events-logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        <main className="events-main">
          <div className="events-content-wrapper">
            {/* Form Section */}
            <div className="events-form-section">
              <div className="events-form-card">
                <h2>
                  {editingId ? "✏️ Edit Event" : "🎉 Create New Event"}
                </h2>
                <form onSubmit={handleSubmit} className="events-form">
                  <div className="events-form-grid">
                    <div className="events-form-group">
                      <label>Event Name *</label>
                      <input
                        type="text"
                        name="eventName"
                        value={formData.eventName}
                        onChange={handleFormChange}
                        placeholder="e.g., Wedding Reception"
                        required
                      />
                    </div>

                    {/* Event Type — Searchable Dropdown */}
                    <div className="events-form-group" ref={typeDropdownRef}>
                      <label>Event Type</label>
                      <div className="events-type-dropdown">
                        <input
                          type="text"
                          className="events-type-search"
                          placeholder="Search or add event type..."
                          value={typeSearch}
                          onChange={(e) => {
                            setTypeSearch(e.target.value);
                            setTypeDropdownOpen(true);
                            if (!e.target.value) setFormData({ ...formData, eventTypeId: "" });
                          }}
                          onFocus={() => setTypeDropdownOpen(true)}
                        />
                        {typeDropdownOpen && (
                          <div className="events-type-list">
                            {filteredTypes.length > 0 ? (
                              filteredTypes.map((et) => (
                                <div
                                  key={et.id}
                                  className={`events-type-item ${formData.eventTypeId === et.id ? "selected" : ""}`}
                                  onClick={() => selectEventType(et)}
                                >
                                  {et.name}
                                </div>
                              ))
                            ) : (
                              <div className="events-type-empty">No matching event types</div>
                            )}
                            {typeSearch.trim() && !exactTypeMatch && (
                              <div className="events-type-item events-type-add" onClick={createAndSelectType}>
                                + Create "<strong>{typeSearch.trim()}</strong>"
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="events-form-group">
                      <label>Groom Name *</label>
                      <input
                        type="text"
                        name="groomName"
                        value={formData.groomName}
                        onChange={handleFormChange}
                        placeholder="Enter groom's name"
                        required
                      />
                    </div>

                    <div className="events-form-group">
                      <label>Bridal Name *</label>
                      <input
                        type="text"
                        name="bridalName"
                        value={formData.bridalName}
                        onChange={handleFormChange}
                        placeholder="Enter bride's name"
                        required
                      />
                    </div>

                    <div className="events-form-group">
                      <label>Event Date *</label>
                      <input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                  </div>

                  {!editingId && uncompressedQuota?.allowed && (
                    <div className="events-form-group events-form-group-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={wantUncompressed}
                          disabled={uncompressedQuota.remaining <= 0}
                          onChange={(e) => setWantUncompressed(e.target.checked)}
                        />
                        {" "}Upload original quality (uncompressed) for this event
                        {" "}— {uncompressedQuota.max === null ? "Unlimited" : `${uncompressedQuota.remaining} of ${uncompressedQuota.max} left this month`}
                      </label>
                    </div>
                  )}

                  {error && <div className="events-error-message">{error}</div>}
                  {success && <div className="events-success-message">{success}</div>}

                  <div className="events-form-buttons">
                    <button
                      type="submit"
                      disabled={loading}
                      className="events-btn-submit"
                    >
                      {loading ? "Processing..." : editingId ? "Update Event" : "Create Event"}
                    </button>
                    {editingId && (
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="events-btn-cancel"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Events List Section */}
            <div className="events-list-section">
              <div className="events-list-header">
                <h2>📋 Your Events ({events.length})</h2>
              </div>

              {fetchLoading ? (
                <CameraLoader text="Loading events..." />
              ) : events.length === 0 ? (
                <div className="events-empty-state">
                  <p className="events-empty-icon">🎊</p>
                  <p className="events-empty-text">No events yet. Create your first event!</p>
                </div>
              ) : (
                <div className="events-grid">
                  {events.map((event) => {
                    const isInlineEditing = inlineEdit && inlineEdit.id === event.id;

                    return (
                      <div
                        key={event.id}
                        className={`events-card ${isInlineEditing ? "events-card-editing" : ""}`}
                        onClick={() => handleOpenEventDetails(event)}
                        style={{ cursor: isInlineEditing ? "default" : "pointer" }}
                      >
                        {event.images && event.images.length > 0 && !isInlineEditing && (
                          <div className="events-card-image-wrapper">
                            <img
                              src={event.images[0].image_url}
                              alt={event.event_name}
                              className="events-card-image"
                            />
                            {event.image_count > 1 && (
                              <span className="events-image-count">+{event.image_count - 1}</span>
                            )}
                          </div>
                        )}

                        {isInlineEditing ? (
                          /* Inline Edit Mode */
                          <div className="events-inline-edit" onClick={(e) => e.stopPropagation()}>
                            <div className="events-inline-field">
                              <label>Event Name</label>
                              <input
                                type="text"
                                value={inlineEdit.eventName}
                                onChange={(e) => handleInlineChange("eventName", e.target.value)}
                              />
                            </div>
                            <div className="events-inline-field">
                              <label>Event Type</label>
                              <select
                                value={inlineEdit.eventTypeId}
                                onChange={(e) => handleInlineChange("eventTypeId", e.target.value)}
                              >
                                <option value="">-- None --</option>
                                {eventTypes.map((et) => (
                                  <option key={et.id} value={et.id}>{et.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="events-inline-row">
                              <div className="events-inline-field">
                                <label>Groom</label>
                                <input
                                  type="text"
                                  value={inlineEdit.groomName}
                                  onChange={(e) => handleInlineChange("groomName", e.target.value)}
                                />
                              </div>
                              <div className="events-inline-field">
                                <label>Bride</label>
                                <input
                                  type="text"
                                  value={inlineEdit.bridalName}
                                  onChange={(e) => handleInlineChange("bridalName", e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="events-inline-field">
                              <label>Date</label>
                              <input
                                type="date"
                                value={inlineEdit.eventDate}
                                onChange={(e) => handleInlineChange("eventDate", e.target.value)}
                              />
                            </div>
                            <div className="events-inline-actions">
                              <button className="events-inline-save" onClick={saveInlineEdit}>✓ Save</button>
                              <button className="events-inline-cancel" onClick={cancelInlineEdit}>✕ Cancel</button>
                            </div>
                          </div>
                        ) : (
                          /* Display Mode */
                          <>
                            <div className="events-card-header">
                              <h3>{event.event_name}</h3>
                              <span className="events-card-date">
                                {event.event_date
                                  ? new Date(event.event_date).toLocaleDateString()
                                  : "No date"}
                              </span>
                            </div>

                            {event.event_type_name && (
                              <div className="events-card-type-tag">{event.event_type_name}</div>
                            )}

                            {Number(event.has_pdf) > 0 && (
                              <div className={`events-card-pdf-badge ${Number(event.pdf_review_count) > 0 ? "completed" : "pending"}`}>
                                {Number(event.pdf_review_count) > 0 ? `✅ ${event.pdf_review_count} Review${Number(event.pdf_review_count) > 1 ? "s" : ""}` : "⏳ PDF Pending Review"}
                              </div>
                            )}

                            <div className="events-card-body">
                              <div className="events-card-item">
                                <span className="events-card-label">👰 Bride:</span>
                                <span className="events-card-value">{event.bridal_name}</span>
                              </div>
                              <div className="events-card-item">
                                <span className="events-card-label">🤵 Groom:</span>
                                <span className="events-card-value">{event.groom_name}</span>
                              </div>
                              {event.image_count > 0 && (
                                <div className="events-card-item">
                                  <span className="events-card-label">📸 Photos:</span>
                                  <span className="events-card-value">{event.image_count}</span>
                                </div>
                              )}
                            </div>

                            <div className="events-card-footer">
                              <span className="events-card-action-hint">View & Upload Photos →</span>
                              <div className="events-card-btns">
                                <button className="events-card-share-btn" onClick={(e) => handleShare(event, e)} title="Share upload link with co-worker" aria-label={`Share ${event.event_name} with a co-worker`}>🔗</button>
                                {canAssign && (
                                  <button className="events-card-assign-btn" onClick={(e) => handleAssign(event, e)} title="Assign staff to this event" aria-label={`Assign staff to ${event.event_name}`}>👥</button>
                                )}
                                <button className="events-card-edit-btn" onClick={(e) => startInlineEdit(event, e)} title="Edit">✏️</button>
                                <button className="events-card-delete-btn" onClick={(e) => handleDelete(event, e)} title="Delete">🗑</button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {shareTarget && (
        <ShareUploadLinkModal event={shareTarget} onClose={() => setShareTarget(null)} />
      )}

      {assignTarget && (
        <AssignStaffModal event={assignTarget} onClose={() => setAssignTarget(null)} />
      )}

      <SubscriptionPausedModal
        open={!!pausedInfo}
        message={pausedInfo?.message}
        locked={pausedInfo?.locked}
        onClose={() => setPausedInfo(null)}
      />

      <ConfirmModal
        open={!!deleteTarget}
        title={deleteTarget ? `Delete "${deleteTarget.event_name}"?` : ""}
        message={deleteTarget ? [
          "This permanently removes:",
          (deleteTarget.image_count || 0) > 0
            ? `• ${deleteTarget.image_count} photo${deleteTarget.image_count === 1 ? "" : "s"} (including any RAW camera originals)`
            : "• All photos",
          "• All videos",
          "• Any uploaded PDF and its client reviews",
          "• Any contract, including a signed copy",
          "• Client selections, share links, and analytics for this event",
          "",
          "This cannot be undone.",
        ] : []}
        confirmLabel="Delete Event"
        loadingLabel="Deleting..."
        danger
        loading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) { setDeleteTarget(null); setDeleteError(""); } }}
      />
    </div>
  );
}

export default Events;
